// Top-bar radio mini-player — subscribes to the shell-level radio engine
// (apps/retailer/src/shell/radio.ts) and renders a compact play/pause +
// station name button. Always visible; ghosted when no station is selected.
//
// Click the play/pause control: toggle play state.
// Click the station label area: navigate to the dashboard (full picker).
// Source-of-truth for the underlying engine + presets: portal-standalone.html
// L13740-13895. The mini-player itself is new — the source did not have a
// persistent player after the old header chip was removed (see source L496
// note "Old header radio chip removed").

import { renderIcon } from "@tkos/shared-ui";

import { subscribe, toggle, type RadioState } from "./radio";

interface MiniPlayerOpts {
  onPickerRequested: () => void;
}

export function createRadioMiniPlayer(opts: MiniPlayerOpts): HTMLElement {
  const root = document.createElement("div");
  root.className = "tkos-mini-radio";
  root.setAttribute("aria-label", "Radio");

  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "tkos-mini-radio__play";
  playBtn.title = "Play / Pause radio";
  playBtn.setAttribute("aria-label", "Play / Pause radio");

  const label = document.createElement("button");
  label.type = "button";
  label.className = "tkos-mini-radio__label";
  label.title = "Open station picker";
  label.setAttribute("aria-label", "Open radio station picker");

  const stationName = document.createElement("span");
  stationName.className = "tkos-mini-radio__name";

  const stationSub = document.createElement("span");
  stationSub.className = "tkos-mini-radio__sub";

  label.append(stationName, stationSub);
  root.append(playBtn, label);

  function paintIcon(state: RadioState): void {
    const playing = state.status === "playing" || state.status === "loading";
    playBtn.innerHTML = "";
    playBtn.appendChild(renderIcon(playing ? "minus" : "play", { size: "sm" }));
    // For "playing" we want a pause glyph. The TKOS icon set doesn't have a
    // dedicated pause yet — two short vertical bars are rendered inline so we
    // don't ship a new icon for one callsite.
    if (playing) {
      playBtn.innerHTML =
        '<svg viewBox="0 0 24 24" class="tkos-icon tkos-icon--sm" aria-hidden="true">' +
        '<rect x="6" y="4.5" width="4" height="15" rx="0.8"/>' +
        '<rect x="14" y="4.5" width="4" height="15" rx="0.8"/>' +
        "</svg>";
    }
  }

  function render(state: RadioState): void {
    const has = !!state.current;
    root.classList.toggle("tkos-mini-radio--has-station", has);
    root.classList.toggle("tkos-mini-radio--playing", state.status === "playing");
    root.classList.toggle("tkos-mini-radio--loading", state.status === "loading");
    root.classList.toggle("tkos-mini-radio--idle", !has);

    stationName.textContent = state.current?.name ?? "Radio";
    if (state.status === "loading") stationSub.textContent = "Connecting…";
    else if (state.status === "playing") stationSub.textContent = "Now playing";
    else if (state.status === "paused") stationSub.textContent = "Paused";
    else if (state.status === "error") stationSub.textContent = "Stream error";
    else stationSub.textContent = has ? "Tap play" : "Tap to pick";

    paintIcon(state);
  }

  const unsub = subscribe(render);

  playBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggle();
  });

  label.addEventListener("click", (e) => {
    e.stopPropagation();
    opts.onPickerRequested();
  });

  // Detach on element removal (basic best-effort — the topbar lives for the
  // session, so this rarely fires, but it keeps the listener count tidy if
  // the topbar is ever torn down).
  const observer = new MutationObserver(() => {
    if (!root.isConnected) {
      unsub();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return root;
}
